/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { apiClient } from '@/lib/apiClient';

/** Captured feedback form; field names match the backend `FeedbackForm`. */
export interface FeedbackForm {
  date?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  secondaryEmail?: string;
  phone?: string;
  alternatePhone?: string;
  role?: string;
  dealerName?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  /** ISO 3166-1 alpha-2, so an address outside the US is unambiguous. */
  country?: string;
  vin?: string;
  year?: string;
  make?: string;
  model?: string;
  program?: string;
  engineeringRelease?: string;
  urlViewSelection?: string;
  issueDescription?: string;
}

/** Feedback submission body. `userId` is the caller's GMIN, `type` is INTERNAL or EXTERNAL. */
export interface UserFeedbackRequest {
  feedbackForm: FeedbackForm;
  url: string;
  userId: string;
  userName: string;
  type: 'INTERNAL' | 'EXTERNAL';
}

/** Feedback classification: INTERNAL = GM service users, EXTERNAL = dealers. */
export type FeedbackType = 'INTERNAL' | 'EXTERNAL';

/** A single row for the admin feedback viewer (matches backend `FeedbackSummary`). */
export interface FeedbackSummary {
  feedbackId: number;
  type: FeedbackType | string;
  userName: string | null;
  userId: string | null;
  url: string | null;
  submissionDate: string | null;
  issueDescription: string | null;
  dealerName: string | null;
  vin: string | null;
  /** Deployment (LOCAL / DEV / TEST / PROD); null for older rows without environment. */
  environment: string | null;
}

/** Attachment metadata for the admin detail panel (download via {@link downloadFeedbackDoc}). */
export interface FeedbackAttachment {
  documentId: number;
  name: string | null;
  type: string | null;
}

/** Full feedback record for the admin detail panel (matches backend `FeedbackDetail`). */
export interface FeedbackDetail {
  feedbackId: number;
  type: FeedbackType | string;
  userName: string | null;
  userId: string | null;
  url: string | null;
  submissionDate: string | null;
  /** Deployment (LOCAL / DEV / TEST / PROD); null for older rows without environment. */
  environment: string | null;
  form: FeedbackForm | null;
  attachments: FeedbackAttachment[];
}

/**
 * Lists recent feedback submissions (admin only), newest first. A deployment reads the database it
 * is wired to, which is not the same as the set of apps that write to it: a developer running
 * locally points at a deployed database, so their submissions land in this list too. See
 * {@link isLocalSubmission}.
 */
export async function listFeedback(
  params: { type?: FeedbackType; limit?: number } = {},
): Promise<FeedbackSummary[]> {
  const { data } = await apiClient.get<FeedbackSummary[]>('/ediagnostics/v1/user-feedback', {
    params: { type: params.type, limit: params.limit ?? 200 },
  });
  return data;
}

/**
 * Whether a row was filed from a workstation instead of from a deployed app. The backend stamps
 * `environment` with the environment of the app that wrote the row, which is the only thing that
 * separates a developer's test submission from a real report in a shared database. Rows written
 * before that column existed carry no stamp, so fall back to the page they were filed from.
 *
 * Only these rows can be deleted. `FeedbackService.isWorkstationSubmission` applies the same rule
 * server-side and answers 409 otherwise, so keep the two in step; this one only hides the button.
 */
export function isLocalSubmission(row: {
  environment: string | null;
  url: string | null;
}): boolean {
  const stamp = row.environment?.trim().toUpperCase();
  if (stamp) {
    return stamp === 'LOCAL';
  }
  return /\/\/(localhost|127\.0\.0\.1|\[::1\])([:/?#]|$)/i.test(row.url ?? '');
}

/** Permanently removes a submission and its attachments (admin only). */
export async function deleteFeedback(id: number): Promise<void> {
  await apiClient.delete(`/ediagnostics/v1/user-feedback/${id}`);
}

/** Full feedback record with attachment metadata (admin only). */
export async function getFeedbackDetail(id: number): Promise<FeedbackDetail> {
  const { data } = await apiClient.get<FeedbackDetail>(`/ediagnostics/v1/user-feedback/${id}`);
  return data;
}

/** Downloads a single attachment, prompting the browser to save it under its original name. */
export async function downloadFeedbackDoc(
  id: number,
  documentId: number,
  name: string | null,
): Promise<void> {
  const { data } = await apiClient.get<Blob>(
    `/ediagnostics/v1/user-feedback/${id}/docs/${documentId}`,
    { responseType: 'blob' },
  );
  const href = URL.createObjectURL(data);
  try {
    const anchor = document.createElement('a');
    anchor.href = href;
    anchor.download = name || 'attachment';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(href);
  }
}

/** Server-side result of the consolidated submit (matches backend `FeedbackSubmitResult`). */
export interface FeedbackSubmitResult {
  feedbackId: number;
  /** True when there were no attachments, or every attachment was stored server-side. */
  attachmentsStored: boolean;
  /** True when the notification email was dispatched (false when mail is disabled/failed). */
  emailSent: boolean;
}

/** Outcome of {@link submitFeedbackFlow}, so callers can tell a clean submit from a partial one. */
export interface FeedbackFlowResult {
  /** New feedback id. The record is persisted and visible in the admin feedback viewer. */
  id: number;
  /** True when there were no attachments, or every attachment was stored server-side. */
  attachmentsUploaded: boolean;
  /**
   * True when the notification email was dispatched. The feedback is persisted either way, so this
   * is for diagnostics rather than for the submitter.
   */
  emailSent: boolean;
}

/** Submits the feedback and its attachments in one request; the backend does the rest. */
export async function submitFeedbackFlow(
  request: UserFeedbackRequest,
  files: File[] = [],
): Promise<FeedbackFlowResult> {
  const form = new FormData();
  // Typed part so the backend binds the JSON directly.
  form.append('feedback', new Blob([JSON.stringify(request)], { type: 'application/json' }));
  files.forEach((file) => form.append('documents', file));
  // No explicit Content-Type: the browser has to set it to supply the multipart boundary.
  const { data } = await apiClient.post<FeedbackSubmitResult>(
    '/ediagnostics/v1/user-feedback/submit',
    form,
  );
  return {
    id: data.feedbackId,
    attachmentsUploaded: data.attachmentsStored,
    emailSent: data.emailSent,
  };
}
